/**
 * Hook für klickbare Spaltenköpfe — die React-Hälfte der Tabellen-Sortierung.
 *
 * Die Regeln selbst (Bedienung, fehlende Werte, Gleichstand) stehen React-frei
 * in `lib/tabellen-sortierung.ts`. Getrennt seit 10.09.2026: Das Release Gate
 * auf GitHub führt die Unit-Tests aus, BEVOR es die UI-Pakete installiert.
 * Stand dieser Hook mit in der Datei der Regeln, scheiterte schon der Import
 * des Tests an `react`. Lokal fiel das nicht auf, weil ui/node_modules dort
 * vorhanden ist.
 */
import { useCallback, useMemo, useState } from "react";
import {
  type Richtung,
  type SortSpalten,
  type Sortierung,
  ersteRichtung,
  sortiereZeilen,
} from "../lib/tabellen-sortierung.js";

export function useTabellenSortierung<T>(
  spalten: SortSpalten<T>,
  /** Ohne aktive Sortierung gilt diese Reihenfolge (z. B. Auffällige zuerst). */
  standard: (a: T, b: T) => number,
) {
  const [sortierung, setSortierung] = useState<Sortierung | null>(null);

  const klick = useCallback(
    (spalte: string) => {
      setSortierung((bisher) => {
        const erste = ersteRichtung(spalten[spalte]);
        if (bisher?.spalte !== spalte) return { spalte, richtung: erste };
        // Zweiter Klick dreht um, dritter geht zur Standardsortierung zurück.
        const gedreht: Richtung = bisher.richtung === "auf" ? "ab" : "auf";
        return gedreht === erste ? null : { spalte, richtung: gedreht };
      });
    },
    [spalten],
  );

  const sortiere = useCallback(
    (zeilen: readonly T[]): T[] => sortiereZeilen(zeilen, spalten, sortierung, standard),
    [sortierung, spalten, standard],
  );

  /** Richtung einer Spalte für den Pfeil im Kopf; null = nicht sortiert. */
  const richtungVon = useCallback(
    (spalte: string): Richtung | null =>
      sortierung?.spalte === spalte ? sortierung.richtung : null,
    [sortierung],
  );

  /** Zurück zur Standardsortierung — etwa wenn die Ansicht wechselt. */
  const zuruecksetzen = useCallback(() => setSortierung(null), []);

  return useMemo(
    () => ({ sortierung, klick, sortiere, richtungVon, zuruecksetzen }),
    [sortierung, klick, sortiere, richtungVon, zuruecksetzen],
  );
}
